import { Injectable, computed, inject, OnDestroy, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { HubConnectionState } from '@microsoft/signalr';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import {
  Notification,
  NotificationEvents,
  NotificationSignalPayload,
  NotificationType,
  UnreadCountResponse
} from '../models/notification.model';
import { ApiService } from '../../../core/http/services/api.service';
import { SignalRService } from '../../../core/realtime/signalr.service';
import { formatTimeAgo } from '../../../shared/utils/date.utils';
import { LoggerService } from '../../../core/logging/services/logger.service';

interface ApiNotificationActor {
  id: string;
  name: string;
  avatarUrl?: string;
  initials?: string;
  role?: string;
}

interface ApiNotificationItem {
  id: string;
  type: NotificationType;
  typeKey: string;
  priority: number;
  title: string;
  body: string;
  actionUrl?: string;
  referenceId?: string;
  referenceType?: string;
  actor?: ApiNotificationActor;
  
  imageUrl?: string;
  metadata: Record<string, string>;
  isRead: boolean;
  readAt?: string;
  createdAt: string;
  timeAgo: string;
  icon: string;
  color: string;
}

interface ApiNotificationListResponse {
  items: ApiNotificationItem[];
  totalCount: number;
  unreadCount: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}


const PANEL_PAGE_SIZE = 10;


const PANEL_MAX_ITEMS = 50;


function sortByCreatedAtDesc(items: Notification[]): Notification[] {
  return [...items].sort((a, b) => {
    const ta = new Date(a.createdAt).getTime();
    const tb = new Date(b.createdAt).getTime();
    const va = Number.isFinite(ta) ? ta : 0;
    const vb = Number.isFinite(tb) ? tb : 0;
    return vb - va;
  });
}


@Injectable({ providedIn: 'root' })
export class NotificationStoreService implements OnDestroy {
  private readonly api = inject(ApiService);
  private readonly signalR = inject(SignalRService);
  private readonly logger = inject(LoggerService);
  private readonly destroy$ = new Subject<void>();


  private readonly _notifications = signal<Notification[]>([]);
  private readonly _unreadCount = signal<number>(0);
  private readonly _highPriorityCount = signal<number>(0);
  private readonly _isLoading = signal<boolean>(false);
  private readonly _isPanelOpen = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);
  private readonly _hasMore = signal<boolean>(false);
  private readonly _currentPage = signal<number>(1);
  private readonly _activeTab = signal<'all' | 'unread' | 'mentions'>('all');

  
  private readonly _seenIds = new Set<string>();

  private readonly _markReadSubject = new Subject<string[]>();
  private _subscribed = false;
  private readonly timeAgoRefreshInterval = window.setInterval(() => {
    this.refreshRelativeTimes();
  }, 1000);


  readonly notifications = this._notifications.asReadonly();
  readonly unreadCount = this._unreadCount.asReadonly();
  readonly highPriorityCount = this._highPriorityCount.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly isPanelOpen = this._isPanelOpen.asReadonly();
  readonly error = this._error.asReadonly();
  readonly hasMore = this._hasMore.asReadonly();
  readonly currentPage = this._currentPage.asReadonly();
  readonly activeTab = this._activeTab.asReadonly();


  readonly filteredNotifications = computed(() => {
    const all = this._notifications();
    const tab = this._activeTab();

    switch (tab) {
      case 'unread':
        return all.filter(n => !n.isRead);
      case 'mentions':
        return all.filter(n => n.title.includes('@') || n.body.includes('@'));
      default:
        return all;
    }
  });

  readonly filteredUnreadCount = computed(() => {
    return this.filteredNotifications().filter(n => !n.isRead).length;
  });

  readonly hasUnread = computed(() => this._unreadCount() > 0);

  readonly isConnected = computed(() =>
    this.signalR.connectionState() === HubConnectionState.Connected
  );


  constructor() {
    this.initializeSignalRListeners();
    this.setupMarkReadDebounce();

    if (typeof window !== 'undefined') {
      window.addEventListener('app:logout', () => {
        this.clear();
      });
    }
  }

  clear(): void {
    this._notifications.set([]);
    this._unreadCount.set(0);
    this._highPriorityCount.set(0);
    this._isPanelOpen.set(false);
    this._hasMore.set(false);
    this._currentPage.set(1);
    this._error.set(null);
    this._seenIds.clear();
    this._subscribed = false;
  }

  /**
   * Call this method when the user is authenticated and needs notifications.
   * Should be called from a component or service that checks auth state first.
   */
  initializeNotifications(): void {
    if (this._subscribed) return;
    this.logger.debug('NotificationStore: Initializing notifications');
    this.ensureSignalRSubscribed();
    void this.loadInitial();
  }

  private ensureSignalRSubscribed(): void {
    if (this._subscribed) return;
    this._subscribed = true;

    this.signalR.connect()
      .then(() => this.signalR.subscribeToNotifications())
      .catch((err) => {
        this._subscribed = false;
        this.logger.warn('NotificationStore: Failed to subscribe to notifications', 'NotificationStoreService', { error: err });
      });
  }

  
  private initializeSignalRListeners(): void {
    this.signalR.on<NotificationSignalPayload>(NotificationEvents.NEW, (payload) => {
      const notification = this.signalPayloadToNotification(payload);
      this.addNotificationToStore(notification);
    });

    this.signalR.on<{ id: string; readAt: string }>(NotificationEvents.READ, ({ id, readAt }) => {
      this.markNotificationReadOptimistic(id, readAt);
    });

    this.signalR.on<{ ids: string[]; readAt: string }>(NotificationEvents.BULK_READ, ({ ids, readAt }) => {
      this.markNotificationsReadOptimistic(ids, readAt);
    });

    this.signalR.on<{ id: string; readAt: string }>(NotificationEvents.READ_ALL, () => {
      this.markAllNotificationsReadOptimistic(new Date().toISOString());
    });

    this.signalR.on<{ id: string }>(NotificationEvents.DELETED, ({ id }) => {
      this.removeNotificationOptimistic(id);
    });

    this.signalR.on<{
      requestId?: string;
      actorUserId?: string;
      targetUserId?: string;
      requesterId?: string;
      cancelledAt?: string;
    }>('connection_request_cancelled', (payload) => {
      let removedUnreadCount = 0;
      this._notifications.update(current => {
        const next: Notification[] = [];
        for (const n of current) {
          const isConnNotif =
            n.referenceType?.toLowerCase() === 'connectionrequest' ||
            n.typeKey?.toLowerCase() === 'connectionrequest' ||
            n.type === NotificationType.ConnectionRequest ||
            String(n.type).toLowerCase() === 'connectionrequest' ||
            String(n.type) === '62' ||
            (n.title ?? '').toLowerCase().includes('connection request') ||
            (n.title ?? '').toLowerCase().includes('wants to cook') ||
            (n.title ?? '').toLowerCase().includes('let\'s cook') ||
            (n.title ?? '').toLowerCase().includes('lets cook');

          const matchRequestId = !!(payload.requestId && n.referenceId === payload.requestId);
          const actorId = n.actor?.id?.toLowerCase();
          const reqId = payload.requesterId?.toLowerCase();
          const actorU = payload.actorUserId?.toLowerCase();
          const targetU = payload.targetUserId?.toLowerCase();

          const matchActor = isConnNotif && !!actorId && (actorId === reqId || actorId === actorU || actorId === targetU);

          if (matchRequestId || matchActor) {
            if (!n.isRead) {
              removedUnreadCount++;
            }
            continue;
          }
          next.push(n);
        }
        return next;
      });

      if (removedUnreadCount > 0) {
        this._unreadCount.update(c => Math.max(0, c - removedUnreadCount));
      }
    });

    this.signalR.on<UnreadCountResponse>(NotificationEvents.UNREAD_COUNT, (response) => {
      this._unreadCount.set(response.count);
      this._highPriorityCount.set(response.highPriorityCount);
    });
  }

  
  private setupMarkReadDebounce(): void {
    this._markReadSubject
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((ids) => {
        this.executeMarkRead(ids);
      });
  }



  async loadInitial(): Promise<void> {
    if (this._isLoading()) return;

    this._isLoading.set(true);
    this._error.set(null);

    try {
      const [listResponse, countResponse] = await Promise.all([
        firstValueFrom(this.api.get<ApiNotificationListResponse>(
          '/notifications',
          { page: 1, pageSize: PANEL_PAGE_SIZE }
        )),
        firstValueFrom(this.api.get<UnreadCountResponse>('/notifications/unread-count')),
      ]);

      let apiNotifications = this.transformApiNotifications(listResponse.items).filter(n => {
        const status = (n.metadata?.['connectionStatus'] || n.metadata?.['status'] || '').toLowerCase();
        return status !== 'cancelled' && status !== 'revoked';
      });

      // CRITICAL: Deduplicate API response by content hash to handle backend duplicates
      // Backend sometimes creates duplicate notification records with different IDs
      apiNotifications = this.deduplicateByContentHash(apiNotifications);

      this._notifications.update(current => {
        const merged = new Map<string, Notification>();

        // First add current (SignalR) notifications
        for (const n of current) merged.set(n.id, n);

        // Then add API notifications (Map will overwrite duplicates by ID)
        for (const n of apiNotifications) merged.set(n.id, n);

        const sorted = sortByCreatedAtDesc([...merged.values()]).slice(0, PANEL_MAX_ITEMS);

        // Update seenIds and content hashes
        this._seenIds.clear();
        this._seenContentHashes.clear();
        for (const n of sorted) {
          this._seenIds.add(n.id);
          this._seenContentHashes.add(this.getNotificationContentHash(n));
        }

        return sorted;
      });

      this._hasMore.set(listResponse.hasMore);
      this._currentPage.set(1);
      this._unreadCount.set(countResponse.count);
      this._highPriorityCount.set(countResponse.highPriorityCount);
    } catch (err) {
      this._error.set('Failed to load notifications');
      this.logger.error('Notification load error', 'NotificationStoreService', { error: err });
    } finally {
      this._isLoading.set(false);
    }
  }

  // Track content hashes separately for deduplication
  private readonly _seenContentHashes = new Set<string>();

  /**
   * Deduplicate notifications by content hash within a time window.
   * Keeps the first occurrence, removes duplicates with same content.
   */
  private deduplicateByContentHash(notifications: Notification[]): Notification[] {
    const seen = new Set<string>();
    const result: Notification[] = [];

    for (const n of notifications) {
      const hash = this.getNotificationContentHash(n);

      if (!seen.has(hash)) {
        seen.add(hash);
        result.push(n);
      }
      // Skip duplicate content notifications
    }

    return result;
  }

  
  private transformApiNotifications(items: ApiNotificationItem[]): Notification[] {
    return items.map(item => ({
      id: item.id,
      type: item.type,
      typeKey: item.typeKey,
      priority: item.priority,
      title: item.title,
      body: item.body,
      actionUrl: item.actionUrl,
      referenceId: item.referenceId,
      referenceType: item.referenceType,
      actor: item.actor ? {
        id: item.actor.id,
        name: item.actor.name,
        avatarUrl: item.actor.avatarUrl,
        initials: item.actor.initials,
        role: item.actor.role || item.metadata?.['actorRole']
      } : undefined,
      imageUrl: item.imageUrl,
      metadata: item.metadata || {},
      isRead: item.isRead,
      readAt: item.readAt,
      createdAt: item.createdAt,
      timeAgo: this.getTimeAgo(item.createdAt),
      icon: this.getIconForType(item.type),
      color: this.getColorForType(item.type),
    }));
  }

  private getTimeAgo(date: string | Date): string {
    return formatTimeAgo(date);
  }

  private refreshRelativeTimes(): void {
    const notifications = this._notifications();
    if (notifications.length === 0) {
      return;
    }

    this._notifications.update(current => {
      let hasChanges = false;

      const updated = current.map(notification => {
        const nextTimeAgo = this.getTimeAgo(notification.createdAt);
        if (notification.timeAgo === nextTimeAgo) {
          return notification;
        }

        hasChanges = true;
        return { ...notification, timeAgo: nextTimeAgo };
      });

      return hasChanges ? updated : current;
    });
  }

  private getIconForType(type: NotificationType): string {
    switch (type) {
      case NotificationType.MessageReceived: return 'message-circle';
      case NotificationType.PaymentReceived:
      case NotificationType.PaymentReleased: return 'credit-card';
      case NotificationType.CollaborationInvite:
      case NotificationType.CollaborationAccepted: return 'users';
      default: return 'bell';
    }
  }

  private getColorForType(type: NotificationType): string {
    switch (type) {
      case NotificationType.MessageReceived: return '#8b5cf6';
      case NotificationType.PaymentReceived:
      case NotificationType.PaymentReleased: return '#10b981';
      case NotificationType.ApplicationReceived: return '#3b82f6';
      case NotificationType.SecurityAlert: return '#ef4444';
      default: return '#6b7280';
    }
  }

  
  async loadMore(): Promise<void> {
    if (this._isLoading() || !this._hasMore()) return;

    this._isLoading.set(true);
    const nextPage = this._currentPage() + 1;

    try {
      const response = await firstValueFrom(this.api.get<ApiNotificationListResponse>(
        '/notifications',
        { page: nextPage, pageSize: PANEL_PAGE_SIZE }
      ));

      const newNotifications = this.transformApiNotifications(response.items).filter(n => {
        const status = (n.metadata?.['connectionStatus'] || n.metadata?.['status'] || '').toLowerCase();
        return status !== 'cancelled' && status !== 'revoked';
      });
      this._notifications.update(current => {
        const merged = new Map<string, Notification>();


        for (const n of current) merged.set(n.id, n);


        for (const n of newNotifications) merged.set(n.id, n);

        const sorted = sortByCreatedAtDesc([...merged.values()]).slice(0, PANEL_MAX_ITEMS);


        for (const n of newNotifications) this._seenIds.add(n.id);

        return sorted;
      });
      this._hasMore.set(response.hasMore);
      this._currentPage.set(nextPage);
    } catch (err) {
      this._error.set('Failed to load more notifications');
    } finally {
      this._isLoading.set(false);
    }
  }

  
  async refreshUnreadCount(): Promise<void> {
    try {
      const response = await firstValueFrom(this.api.get<UnreadCountResponse>('/notifications/unread-count'));
      this._unreadCount.set(response.count);
      this._highPriorityCount.set(response.highPriorityCount);
    } catch {
    }
  }



  addNotificationToStore(notification: Notification): void {
    const contentHash = this.getNotificationContentHash(notification);

    // Primary deduplication: check by content hash first (handles backend duplicates)
    if (this._seenContentHashes.has(contentHash)) {
      this._seenIds.add(notification.id); // Mark ID too to prevent future checks
      return;
    }

    // Secondary deduplication: check by unique ID
    if (this._seenIds.has(notification.id)) {
      return;
    }

    // Check if notification already exists in store
    const alreadyPresent = this._notifications().some(n => n.id === notification.id);
    if (alreadyPresent) {
      this._seenIds.add(notification.id);
      this._seenContentHashes.add(contentHash);
      return;
    }

    // All checks passed - add the notification
    this._seenIds.add(notification.id);
    this._seenContentHashes.add(contentHash);

    this._notifications.update(current => {
      const merged = new Map<string, Notification>();
      merged.set(notification.id, notification);
      for (const n of current) merged.set(n.id, n);
      return sortByCreatedAtDesc([...merged.values()]).slice(0, PANEL_MAX_ITEMS);
    });

    if (!notification.isRead) {
      this._unreadCount.update(c => c + 1);
      if (notification.priority >= 2) {
        this._highPriorityCount.update(c => c + 1);
      }
    }
  }

  private getNotificationContentHash(notification: Notification): string {
    const type = notification.typeKey || String(notification.type);
    const refType = notification.referenceType || '';
    const refId = notification.referenceId || '';
    const title = notification.title || '';
    const body = notification.body || '';
    return `${type}|${refType}|${refId}|${title}|${body}`;
  }

  
  private markNotificationReadOptimistic(id: string, readAt: string): void {
    this._notifications.update(current =>
      current.map(n => n.id === id ? { ...n, isRead: true, readAt } : n)
    );
    this.decrementUnreadCount();
  }

  
  private markNotificationsReadOptimistic(ids: string[], readAt: string): void {
    const idSet = new Set(ids);
    let decrementBy = 0;

    this._notifications.update(current =>
      current.map(n => {
        if (idSet.has(n.id) && !n.isRead) {
          decrementBy++;
          return { ...n, isRead: true, readAt };
        }
        return n;
      })
    );

    this._unreadCount.update(c => Math.max(0, c - decrementBy));
  }

  
  private markAllNotificationsReadOptimistic(readAt: string): void {
    this._notifications.update(current =>
      current.map(n => ({ ...n, isRead: true, readAt }))
    );
    this._unreadCount.set(0);
    this._highPriorityCount.set(0);
  }

  
  private removeNotificationOptimistic(id: string): void {
    const notification = this._notifications().find(n => n.id === id);
    this._notifications.update(current => current.filter(n => n.id !== id));

    if (notification && !notification.isRead) {
      this.decrementUnreadCount();
    }
  }

  
  private decrementUnreadCount(): void {
    this._unreadCount.update(c => Math.max(0, c - 1));
    this._highPriorityCount.update(c => Math.max(0, c - 1));
  }


  
  markAsRead(id: string): void {
    const now = new Date().toISOString();
    this.markNotificationReadOptimistic(id, now);
    this._markReadSubject.next([id]);
  }

  
  markAsReadBatch(ids: string[]): void {
    const now = new Date().toISOString();
    this.markNotificationsReadOptimistic(ids, now);
    this._markReadSubject.next(ids);
  }

  
  async markAllAsRead(): Promise<void> {
    const now = new Date().toISOString();
    this.markAllNotificationsReadOptimistic(now);

    try {
      await firstValueFrom(this.api.post('/notifications/read-all', {}));
    } catch {
      await this.loadInitial();
    }
  }

  
  async deleteNotification(id: string): Promise<void> {
    this.removeNotificationOptimistic(id);

    try {
      await firstValueFrom(this.api.delete(`/notifications/${id}`));
    } catch {
      await this.loadInitial();
    }
  }

  
  async deleteNotifications(ids: string[]): Promise<void> {
    const idSet = new Set(ids);
    this._notifications.update(current => current.filter(n => !idSet.has(n.id)));

    try {
      await firstValueFrom(this.api.delete('/notifications', { ids } as any));
    } catch {
      await this.loadInitial();
    }
  }


  setActiveTab(tab: 'all' | 'unread' | 'mentions'): void {
    this._activeTab.set(tab);
  }

  togglePanel(): void {
    const willOpen = !this._isPanelOpen();
    this._isPanelOpen.set(willOpen);
    if (willOpen) {
      this.loadInitial();
    }
  }

  openPanel(): void {
    if (!this._isPanelOpen()) {
      this._isPanelOpen.set(true);
      this.loadInitial();
    }
  }

  closePanel(): void {
    this._isPanelOpen.set(false);
  }


  private async executeMarkRead(ids: string[]): Promise<void> {
    try {
      await firstValueFrom(this.api.post('/notifications/read', { ids }));
    } catch {
    }
  }


  private signalPayloadToNotification(payload: NotificationSignalPayload): Notification {
    const notificationType = NotificationType[payload.type as keyof typeof NotificationType] ?? NotificationType.SystemAnnouncement;
    return {
      id: payload.id,
      type: notificationType,
      typeKey: payload.type,
      priority: payload.priority as any,
      title: payload.title,
      body: payload.body,
      actionUrl: payload.actionUrl,
      referenceId: payload.referenceId,
      referenceType: payload.referenceType,
      actor: payload.actor ? {
        id: payload.actor.id,
        name: payload.actor.name,
        avatarUrl: payload.actor.avatarUrl,
        initials: payload.actor.initials,
        role: payload.actor.role
      } : undefined,
      imageUrl: (payload as { imageUrl?: string }).imageUrl,
      metadata: {},
      isRead: false,
      createdAt: payload.createdAt,
      timeAgo: this.getTimeAgo(payload.createdAt),
      icon: this.getIconForType(notificationType),
      color: this.getColorForType(notificationType),
    };
  }


  ngOnDestroy(): void {
    window.clearInterval(this.timeAgoRefreshInterval);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
